import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId } from 'react';
import cn from 'classnames/bind';
import { useControlledState } from '@sbt-web/hooks';
import { IconSize } from '../../lib/constants.js';
import { Icon as IconMemo } from '../../UIElements/Icon/index.js';
import { InputFooter } from '../InputFooter/index.js';
import { InputHeader } from '../InputHeader/index.js';
import classes from './index.module.scss.js';

const cx = cn.bind(classes);
const sizeToIconSize = (size) => {
    switch (size) {
        case 'small': {
            return IconSize.SM;
        }
        default: {
            return IconSize.MD;
        }
    }
};
const InputIcon = ({ icon, size }) => {
    return (jsx("div", { className: cx('input-icon'), children: jsx(IconMemo, { name: icon, iconSize: sizeToIconSize(size) }) }));
};
const ClearIcon = ({ size, onClick, show, }) => {
    return (jsx(IconButton, { iconName: "Close", size: size, onClick: onClick, ariaLabel: "Svuotare il campo", className: cx('clear-icon', { [classes.show]: show }) }));
};
const IconButton = ({ size, iconName, onClick, className, ariaLabel, }) => {
    const iconSize = sizeToIconSize(size);
    return (jsx("button", { type: "button", onClick: onClick, "aria-label": ariaLabel, className: className, children: jsx(IconMemo, { name: iconName, iconSize: iconSize }) }));
};
const TextField = forwardRef((props, ref) => {
    const { name, type = 'text', icon, inputHeaderProps, maxLength, errorMessage, infoMessage, errorState = false, value, placeholder, disabled = false, size = 'medium', prefix, classes = [], withClearIcon = false, onInputChange = () => undefined, onFocus = () => undefined, onBlur = () => undefined, onCleared = () => undefined, onKeyDown = () => undefined, disableNativeAC = false, autoCapitalize, autoComplete, inputMode, suffixIcon, onSuffixIconClick, } = props;
    const handleInputChange = (newValue) => {
        if (typeof newValue !== 'undefined') {
            onInputChange?.({
                target: { name, value: newValue },
            });
        }
    };
    const [currentValue, setCurrentValue] = useControlledState(value, '', handleInputChange);
    /**
     * The ID of the optional datalist element.
     * If not used, it will be ignored.
     */
    const dataListId = useId();
    const footerMessage = errorMessage || infoMessage;
    return (jsxs("div", { className: cx('cactus-text-field__wrapper', {
            'is-error': errorMessage || errorState,
            'is-disabled': disabled,
            sm: size == 'small',
            md: size == 'medium',
            lg: size == 'large',
        }, ...classes), children: [jsx(InputHeader, { name: name, ...inputHeaderProps, style: errorMessage ? 'red' : 'default' }), jsxs("div", { className: cx('input-wrapper'), children: [icon && jsx(InputIcon, { icon: icon, size: size }), prefix && (jsx("span", { className: cx('prefix', `size-${mapSizeTextSize[size]}`), children: prefix })), jsx("input", { ref: ref, type: type, id: name, name: name, value: currentValue, placeholder: placeholder, disabled: disabled, onChange: (e) => {
                            setCurrentValue(e.target.value);
                        }, maxLength: maxLength, className: cx(`size-${mapSizeTextSize[size]}`, 'weight-book'), onFocus: onFocus, onBlur: onBlur, autoComplete: disableNativeAC ? 'off' : autoComplete, onKeyDown: onKeyDown, autoCapitalize: autoCapitalize, inputMode: inputMode, list: props.suggestions !== undefined ? dataListId : undefined }), props.suggestions !== undefined ? (jsx("datalist", { id: dataListId, children: props.suggestions.map((suggestion) => (jsx("option", { value: suggestion.value, children: suggestion.label }, suggestion.value))) })) : null, suffixIcon && !withClearIcon && (jsx(IconButton, { size: size, iconName: suffixIcon, onClick: onSuffixIconClick, className: cx('suffix-icon', {
                            interactive: !!onSuffixIconClick,
                        }), ariaLabel: "suffix icon" })), withClearIcon && (jsx(ClearIcon, { size: size, onClick: () => {
                            setCurrentValue('');
                            onCleared();
                        }, show: !!currentValue && !disabled }))] }), footerMessage && (jsx(InputFooter, { message: footerMessage, isError: !!errorMessage }))] }));
});
const mapSizeTextSize = {
    small: 'small',
    medium: 'small',
    large: 'normal',
};
TextField.displayName = 'TextField';

export { TextField };
//# sourceMappingURL=index.js.map
